import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tasksApi, { TASK_PRIORITIES } from '../../utils/tasksApi';
import calendarApi, { EVENT_COLORS } from '../../utils/calendarApi';
import { Button, Field, Input, Modal, Select, Textarea, cx, useToast } from './WorkspaceUI';

/**
 * Bridge between Mail and the Tasks / Calendar modules.
 *
 * Rendered inside the legacy mail reader, so the trigger buttons reuse the
 * existing mail toolbar classes while the dialogs use the workspace UI kit.
 * Nothing is created without an explicit confirmation from the user.
 */

const toLocalInput = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const nextHalfHour = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() > 30 ? 60 : 30, 0, 0);
  return date;
};

const plainTextOf = (mail) =>
  String(mail?.body || mail?.snippet || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const TaskDialog = ({ open, mail, onClose }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', notes: '', priority: 'medium', dueDate: '', category: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      title: mail?.subject || '(no subject)',
      notes: [`From: ${mail?.from || ''}`, '', plainTextOf(mail).slice(0, 800)].join('\n'),
      priority: 'medium',
      dueDate: '',
      category: '',
    });
  }, [open, mail]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await tasksApi.createFromMail(mail._id, {
        ...form,
        dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,
      });
      if (res?.success) {
        onClose();
        toast('Task created from email', {
          action: { label: 'Open Tasks', onClick: () => navigate('/tasks') },
        });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create the task.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a task from this email"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy || !form.title.trim()}>
            {busy ? 'Creating…' : 'Create task'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <p role="alert" className="rounded-lg bg-red-500/10 px-3 py-2 text-[12px] text-red-600">
            {error}
          </p>
        )}
        <Field label="Title">
          <Input
            data-autofocus
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Priority">
            <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {TASK_PRIORITIES.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Due date">
            <Input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Category" hint="Optional">
          <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        </Field>
        <Field label="Notes" hint="The email text is copied here so the context travels with the task.">
          <Textarea rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
};

const EventDialogFromMail = ({ open, mail, onClose }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', start: '', end: '', location: '', color: 'blue' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    const start = nextHalfHour();
    setForm({
      title: mail?.subject || '(no subject)',
      start: toLocalInput(start),
      end: toLocalInput(new Date(start.getTime() + 3600000)),
      location: '',
      color: 'blue',
    });
  }, [open, mail]);

  const submit = async () => {
    if (new Date(form.end) < new Date(form.start)) {
      setError('The end time is before the start time.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await calendarApi.createFromMail(mail._id, {
        title: form.title,
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
        location: form.location,
        color: form.color,
      });
      if (res?.success) {
        onClose();
        toast('Event added to your calendar', {
          action: { label: 'Open Calendar', onClick: () => navigate('/calendar') },
        });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create the event.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add this email to your calendar"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy || !form.title.trim()}>
            {busy ? 'Creating…' : 'Create event'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <p role="alert" className="rounded-lg bg-red-500/10 px-3 py-2 text-[12px] text-red-600">
            {error}
          </p>
        )}
        <Field label="Title">
          <Input
            data-autofocus
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Starts">
            <Input
              type="datetime-local"
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
            />
          </Field>
          <Field label="Ends">
            <Input
              type="datetime-local"
              value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Location" hint="Optional">
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </Field>
        <Field label="Colour">
          <div className="flex flex-wrap gap-2">
            {EVENT_COLORS.map((color) => (
              <button
                key={color.id}
                type="button"
                aria-label={color.label}
                aria-pressed={form.color === color.id}
                onClick={() => setForm({ ...form, color: color.id })}
                className={cx(
                  'ws-focus h-7 w-7 rounded-full border-2',
                  form.color === color.id ? 'scale-110 border-ink' : 'border-transparent'
                )}
                style={{ background: color.hex }}
              />
            ))}
          </div>
        </Field>
        <p className="text-[12px] text-ink-soft">
          The email sender is added as a guest and the message body is copied into the description.
          No invitations are sent.
        </p>
      </div>
    </Modal>
  );
};

const MailWorkspaceActions = ({ mail, className = 'reply-btn' }) => {
  const [dialog, setDialog] = useState(null);

  if (!mail?._id) return null;

  return (
    <>
      <button
        type="button"
        className={className}
        title="Create a task from this email"
        onClick={(e) => {
          e.stopPropagation();
          setDialog('task');
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
        </svg>
        <span>Add to Tasks</span>
      </button>

      <button
        type="button"
        className={className}
        title="Add this email to your calendar"
        onClick={(e) => {
          e.stopPropagation();
          setDialog('event');
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5z" />
        </svg>
        <span>Add to Calendar</span>
      </button>

      <TaskDialog open={dialog === 'task'} mail={mail} onClose={() => setDialog(null)} />
      <EventDialogFromMail open={dialog === 'event'} mail={mail} onClose={() => setDialog(null)} />
    </>
  );
};

export default MailWorkspaceActions;
